import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Minus, Plus, ShoppingCart, Flame } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK_PRODUCTS = [
  {
    id: "tomato-chutney-with-garlic",
    name: "Tomato Chutney with Garlic",
    description:
      "Slow-cooked ripe tomatoes tempered with fresh garlic, curry leaves, and Byadgi chilies. Bold, tangy, and irresistible.",
    price: 299,
    image: "https://customer-assets.emergentagent.com/job_halli-chutney-site/artifacts/zcldzaps_WhatsApp%20Image%202026-07-03%20at%209.16.14%20PM.jpeg",
    weight: "250g",
    spice_level: "Medium",
  },
  {
    id: "tomato-chutney-without-garlic",
    name: "Tomato Chutney without Garlic",
    description:
      "The same classic tomato chutney — hand-pounded and slow-simmered — crafted garlic-free for a milder, family-friendly flavour.",
    price: 299,
    image: "https://customer-assets.emergentagent.com/job_halli-chutney-site/artifacts/4ztqw3xw_Screenshot%202026-07-03%20212642.png",
    weight: "250g",
    spice_level: "Mild",
  },
  {
    id: "amla-tomato-chutney-with-garlic",
    name: "Amla Tomato Chutney with Garlic",
    description:
      "Tangy amla, ripe tomatoes, and pungent garlic come together in a wellness-forward chutney that's sharp, sour, and deeply satisfying.",
    price: 299,
    image: "https://customer-assets.emergentagent.com/job_halli-chutney-site/artifacts/zkjunulw_WhatsApp%20Image%202026-07-03%20at%205.21.14%20PM.jpeg",
    weight: "250g",
    spice_level: "Hot",
  },
  {
    id: "amla-tomato-chutney-without-garlic",
    name: "Amla Tomato Chutney without Garlic",
    description:
      "Antioxidant-rich amla balanced with slow-roasted tomatoes and Karnataka spices — a garlic-free, gut-friendly village recipe.",
    price: 299,
    image: "https://customer-assets.emergentagent.com/job_halli-chutney-site/artifacts/9j748fh9_Screenshot%202026-07-03%20212707.png",
    weight: "250g",
    spice_level: "Mild",
  },
];

const SpiceBadge = ({ level }) => {
  const flames = level === "Hot" ? 3 : level === "Medium" ? 2 : 1;
  return (
    <span className="inline-flex items-center gap-0.5 text-xs bg-stone-100 text-stone-700 px-2.5 py-1 rounded-full">
      {[...Array(3)].map((_, i) => (
        <Flame
          key={i}
          className={`w-3 h-3 ${i < flames ? "text-[#993311]" : "text-stone-300"}`}
          fill={i < flames ? "currentColor" : "none"}
        />
      ))}
      <span className="ml-1">{level}</span>
    </span>
  );
};

const ProductCard = ({ product, index }) => {
  const { addItem } = useCart();
  const [qty, setQty] = useState(1);

  const handleAdd = () => {
    if (qty > 20) {
      toast.error("Maximum 20 jars per order.");
      return;
    }
    addItem(product, qty);
    setQty(1);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className="group bg-white border border-stone-200/70 rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-500 flex flex-col"
      data-testid={`product-card-${product.id}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-stone-100">
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
        />
        <div className="absolute top-3 right-3 bg-stone-50/90 backdrop-blur text-stone-800 text-xs px-2.5 py-1 rounded-full">
          {product.weight}
        </div>
      </div>

      <div className="p-6 flex flex-col flex-1">
        <h3 className="font-serif text-xl md:text-2xl text-stone-900 leading-tight">
          {product.name}
        </h3>
        <p className="mt-2 text-sm text-stone-600 leading-relaxed flex-1">
          {product.description}
        </p>

        <div className="mt-5 flex items-baseline gap-2">
          <span
            className="font-serif text-2xl md:text-3xl text-[#993311] font-semibold"
            data-testid={`product-price-${product.id}`}
          >
            ₹{product.price}
          </span>
          <span className="text-xs text-stone-500">/ jar</span>
        </div>

        <div className="mt-5 flex items-center gap-3">
          <div className="flex items-center rounded-full border border-stone-300 bg-stone-50">
            <button
              onClick={() => setQty(Math.max(1, qty - 1))}
              className="w-9 h-9 grid place-items-center text-stone-700 hover:text-[#993311] transition-colors"
              data-testid={`product-qty-decrease-${product.id}`}
              aria-label="Decrease quantity"
            >
              <Minus className="w-3.5 h-3.5" />
            </button>
            <span
              className="w-8 text-center text-sm font-medium tabular-nums"
              data-testid={`product-qty-value-${product.id}`}
            >
              {qty}
            </span>
            <button
              onClick={() => setQty(Math.min(20, qty + 1))}
              className="w-9 h-9 grid place-items-center text-stone-700 hover:text-[#993311] transition-colors"
              data-testid={`product-qty-increase-${product.id}`}
              aria-label="Increase quantity"
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          </div>
          <Button
            onClick={handleAdd}
            className="flex-1 bg-[#993311] hover:bg-[#7A290E] text-stone-50 rounded-full h-11 gap-2 transition-transform hover:-translate-y-0.5"
            data-testid={`add-to-cart-btn-${product.id}`}
          >
            <ShoppingCart className="w-4 h-4" />
            Add to Cart
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

export default function Products() {
  const [products, setProducts] = useState(FALLBACK_PRODUCTS);

  useEffect(() => {
    axios
      .get(`${API}/products`)
      .then((r) => {
        if (Array.isArray(r.data) && r.data.length > 0) setProducts(r.data);
      })
      .catch(() => {});
  }, []);

  return (
    <section
      id="shop"
      className="py-20 md:py-32 bg-[#faf5ef] relative"
      data-testid="products-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <span className="font-hand text-2xl text-[#993311]">— shop the jars</span>
          <h2 className="mt-2 font-serif text-3xl md:text-4xl lg:text-5xl leading-tight text-stone-900">
            Small-batch, <span className="italic">big flavour.</span>
          </h2>
          <p className="mt-4 text-stone-600 md:text-lg">
            Four signature recipes, freshly bottled every week. Choose your jar.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {products.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
